/**
 * Iter 83.2B — Project Inbox (per-project command center).
 *
 * Renders a tight, project-scoped notification feed (mentions · punch ·
 * exports · sharing · plans · closeout) plus a Watch/Unwatch control.
 * Drop-in component — mount inside Project.jsx via the existing tab
 * structure or as a side-panel.
 *
 * Visual DNA: inherits NotificationsBell aesthetic verbatim. Same
 * filter pills, same row layout, same orange `@user` highlighting.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AtSign, BellOff, BellRing, FileText, GitBranch, Inbox,
  Link2, MessageSquare, Package, Share2,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/utils";

const FILTERS = [
  { key: "all",         label: "All",         type: null         },
  { key: "unread",      label: "Unread",      type: null, status: "unread" },
  { key: "mentions",    label: "Mentions",    type: "mention"    },
  { key: "assignments", label: "Assigned",    type: "punch.assigned" },
  { key: "overdue",     label: "Overdue",     type: "punch.overdue|punch.escalated|punch.due_soon" },
  { key: "punch",       label: "Punch",       type: "punch"      },
  { key: "plans",       label: "Plans",       type: "markup|object" },
  { key: "exports",     label: "Exports",     type: "export"     },
  { key: "sharing",     label: "Sharing",     type: "share"      },
];

const TYPE_ICON = {
  "mention":           AtSign,
  "punch.created":     MessageSquare,
  "punch.assigned":    MessageSquare,
  "punch.updated":     MessageSquare,
  "punch.closed":      MessageSquare,
  "punch.rejected":    MessageSquare,
  "punch.ready_for_review": MessageSquare,
  "punch.due_soon":    MessageSquare,
  "punch.overdue":     MessageSquare,
  "punch.escalated":   MessageSquare,
  "export.completed":  Package,
  "export.failed":     Package,
  "share.created":     Share2,
  "share.revoked":     Share2,
  "markup.created":    GitBranch,
  "object.created":    FileText,
  "object.updated":    FileText,
};

const MENTION_RE = /(@[A-Za-z0-9][A-Za-z0-9._-]*)/g;
function renderMentions(text) {
  if (!text) return null;
  return text.split(MENTION_RE).map((p, i) =>
    p.startsWith("@") ? (
      <span key={i} className="font-medium"
            style={{ color: "var(--fop-primary, #ff6a1a)" }}>
        {p}
      </span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

export default function ProjectInbox({ projectId, compact = false }) {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watching, setWatching] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [counters, setCounters] = useState({});
  const navigate = useNavigate();

  const current = useMemo(() => FILTERS.find((f) => f.key === filter) || FILTERS[0],
                            [filter]);

  const computeCounters = useCallback((rows) => {
    const c = { all: rows.length, unread: 0, mentions: 0,
                assignments: 0, overdue: 0, punch: 0,
                plans: 0, exports: 0, sharing: 0 };
    for (const r of rows) {
      if (!r.read) c.unread += 1;
      const t = r.notification_type || "";
      if (t === "mention") c.mentions += 1;
      if (t === "punch.assigned") c.assignments += 1;
      if (t === "punch.overdue" || t === "punch.escalated"
          || t === "punch.due_soon") c.overdue += 1;
      if (t.startsWith("punch.")) c.punch += 1;
      if (t.startsWith("markup.") || t.startsWith("object.")) c.plans += 1;
      if (t.startsWith("export.")) c.exports += 1;
      if (t.startsWith("share."))  c.sharing += 1;
    }
    return c;
  }, []);

  const fetchInbox = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (current.status === "unread") params.status = "unread";
      // Multi-prefix filters narrow client-side after a wide fetch.
      const isMulti = current.type && current.type.includes("|");
      if (current.type && !isMulti) params.category = current.type;
      const { data } = await api.get(`/projects/${projectId}/inbox`, { params });
      setProject({ id: data.project_id, name: data.project_name });
      let rows = data.items || [];
      if (isMulti) {
        const prefixes = current.type.split("|");
        rows = rows.filter((r) =>
          prefixes.some((p) =>
            r.notification_type === p ||
            r.notification_type?.startsWith(p))
        );
      }
      setItems(rows);
      // Fetch a wider snapshot once per refresh to compute counters.
      if (filter === "all") {
        setCounters(computeCounters(data.items || []));
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, current, filter, computeCounters]);

  const fetchWatch = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/watchlists`, { params: { project_id: projectId } });
      const projectWatch = (data || []).find(
        (w) => w.watch_type === "project" && !w.entity_id
      );
      setWatching(!!projectWatch);
      setWatchId(projectWatch?.id || null);
    } catch { /* silent */ }
  }, [projectId]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);
  useEffect(() => { fetchWatch(); }, [fetchWatch]);

  const handleToggleWatch = async () => {
    try {
      if (watching && watchId) {
        await api.delete(`/watchlists/${watchId}`);
        setWatching(false);
        setWatchId(null);
      } else {
        const { data } = await api.post(`/watchlists`, {
          project_id: projectId, watch_type: "project",
        });
        setWatching(true);
        setWatchId(data.id);
      }
    } catch { /* silent */ }
  };

  const handleOpenItem = async (n) => {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.deep_link) navigate(n.deep_link);
    fetchInbox();
  };

  return (
    <div className="fop-surface-2 rounded-lg border border-[var(--fop-border)]"
         data-testid="project-inbox">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3
                      border-b border-[var(--fop-border)]">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em]
                          text-[var(--fop-text-3)]">
            Project Inbox
          </div>
          <div className="text-sm font-medium text-[var(--fop-text)] mt-0.5">
            {project?.name || "—"}
          </div>
        </div>
        <button
          onClick={handleToggleWatch}
          className={`btn ${watching ? "btn-primary" : "btn-ghost"}
                       text-[11px] uppercase tracking-wider min-h-[36px] px-3`}
          data-testid={watching ? "inbox-unwatch" : "inbox-watch"}
        >
          {watching ? <BellRing size={13} /> : <BellOff size={13} />}
          {watching ? "Watching" : "Watch"}
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex gap-1 px-3 py-2 border-b border-[var(--fop-border)] flex-wrap">
        {FILTERS.map((f) => {
          const count = counters[f.key];
          return (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-[11px] uppercase tracking-wider font-mono
                         px-2.5 py-1 rounded transition-colors inline-flex items-center gap-1.5
                         ${filter === f.key
                           ? "bg-[var(--fop-primary,#ff6a1a)] text-[var(--fop-bg,#0a0a0b)]"
                           : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface)]"}`}
            data-testid={`inbox-filter-${f.key}`}
          >
            {f.label}
            {typeof count === "number" && count > 0 && (
              <span
                data-testid={`inbox-count-${f.key}`}
                className={`text-[9px] tracking-normal font-semibold rounded-sm px-1 ${
                  filter === f.key
                    ? "bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-primary,#ff6a1a)]"
                    : "bg-[var(--fop-surface)] text-[var(--fop-text-2)]"
                }`}
              >
                {count}
              </span>
            )}
          </button>
          );
        })}
      </div>

      {/* List */}
      <ul className={`overflow-y-auto ${compact ? "max-h-[400px]" : "max-h-[640px]"}`}
          data-testid="project-inbox-list">
        {loading ? (
          <li className="px-4 py-10 text-center text-sm text-[var(--fop-text-3)]">
            Loading…
          </li>
        ) : items.length === 0 ? (
          <li className="px-4 py-12 text-center text-sm text-[var(--fop-text-3)]"
              data-testid="project-inbox-empty">
            <Inbox size={20} className="mx-auto mb-2 opacity-50" />
            <div className="font-mono uppercase tracking-wider text-[10px]
                              text-[var(--fop-text-4)] mb-1">
              No activity
            </div>
            <div className="text-xs">
              {watching
                ? "You're watching this project — activity will land here."
                : "Tap Watch to follow this project's activity."}
            </div>
          </li>
        ) : (
          items.map((n) => {
            const Icon = TYPE_ICON[n.notification_type] || Link2;
            return (
              <li key={n.id}>
                <button
                  onClick={() => handleOpenItem(n)}
                  className={`w-full text-left px-3 py-2.5
                                border-b border-[var(--fop-border)]
                                hover:bg-[var(--fop-surface)]
                                transition-colors flex gap-2.5 min-h-[60px]
                                ${n.read ? "opacity-65" : ""}`}
                  data-testid={`inbox-item-${n.id}`}
                >
                  <div className="w-6 h-6 shrink-0 rounded
                                    bg-[var(--fop-surface)]
                                    text-[var(--fop-text-2)]
                                    flex items-center justify-center mt-0.5">
                    <Icon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--fop-text)]
                                      font-medium truncate">
                      {n.title}
                    </div>
                    {n.body && (
                      <div className="text-xs text-[var(--fop-text-2)]
                                        truncate mt-0.5">
                        {renderMentions(n.body)}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] font-mono uppercase
                                          tracking-wider text-[var(--fop-text-4)]">
                        {n.notification_type?.replace(".", " · ")}
                      </span>
                      <span className="text-[10px] font-mono text-[var(--fop-text-4)]">
                        · {formatRelative(n.created_at)}
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
